import { useEffect, useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { MessageCircle } from 'lucide-react'
import { organizationStore } from '@/stores/organization.store'
import { useUnreadSupportCount } from '@/stores/support.store'
import {
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { SupportChatPanel } from './SupportChatPanel'

/**
 * Support chat entry point for the left app sidebar footer. Renders a native
 * sidebar menu button (icon + label, collapses to icon mode) with the unread
 * badge, and opens the reused SupportChatPanel anchored just to the right of
 * the sidebar edge.
 */
export function SidebarSupportChat() {
  const [open, setOpen] = useState(false)
  const { state, isMobile } = useSidebar()
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const orgId = selectedOrganizationId || ''

  const { count: unreadCount } = useUnreadSupportCount(orgId, !open)

  // Close when clicking outside the trigger or the panel. Both carry a
  // `data-support-chat` marker so a single containment check covers them.
  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as HTMLElement | null
      if (target?.closest('[data-support-chat]')) return
      setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  // Anchor the panel just past the sidebar edge. The sidebar width vars live on
  // an ancestor wrapper, so they resolve for this fixed panel too. The inset
  // icon rail is the icon width plus its 1rem of padding. On mobile the
  // sidebar is an overlay, so let the panel's max-sm styles take over.
  const panelStyle = isMobile
    ? undefined
    : {
        left:
          state === 'collapsed'
            ? 'calc(var(--sidebar-width-icon) + 1.5rem)'
            : 'calc(var(--sidebar-width) + 0.5rem)',
      }

  return (
    <>
      <SidebarMenu data-support-chat>
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Support"
            className="rounded-lg"
            isActive={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            <MessageCircle />
            <span>Support</span>
          </SidebarMenuButton>
          {unreadCount > 0 && (
            <SidebarMenuBadge className="rounded-full bg-destructive text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </SidebarMenuBadge>
          )}
          {/* Collapsed-mode indicator — the numeric badge above is hidden in
              icon mode, so surface unread as a small dot over the icon. */}
          {unreadCount > 0 && (
            <span
              className={cn(
                'pointer-events-none absolute right-1.5 top-1.5 hidden size-2 rounded-full bg-destructive',
                'group-data-[collapsible=icon]:block',
              )}
            />
          )}
        </SidebarMenuItem>
      </SidebarMenu>
      <SupportChatPanel open={open} onClose={() => setOpen(false)} anchor="sidebar" style={panelStyle} />
    </>
  )
}
